import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import './HubPages.css';

/**
 * Groups hub — list shared mailboxes and create new ones.
 * Opened from Workspace → Groups (/groups).
 */
const GroupsHub = ({ currentUser }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [memberInput, setMemberInput] = useState('');
  const [members, setMembers] = useState([]);
  const [createError, setCreateError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get('/api/groups');
      if (response.data.success) {
        setGroups(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load groups');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
    const refresh = () => loadGroups();
    window.addEventListener('groupsUpdated', refresh);
    return () => window.removeEventListener('groupsUpdated', refresh);
  }, [loadGroups]);

  const addMember = () => {
    const email = memberInput.trim().toLowerCase().replace(/,$/, '');
    if (!email) return;
    if (!/^[a-zA-Z0-9._-]+@atozasmail\.in$/.test(email)) {
      setCreateError('Only @atozasmail.in users can be added to a group.');
      return;
    }
    if (members.includes(email) || email === currentUser?.email?.toLowerCase()) {
      setMemberInput('');
      return;
    }
    setMembers((prev) => [...prev, email]);
    setMemberInput('');
    setCreateError('');
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setCreateError('Group name is required');
      return;
    }
    setSaving(true);
    setCreateError('');
    try {
      const response = await axiosInstance.post('/api/groups', {
        name: trimmed,
        members,
      });
      if (response.data.success) {
        const created = response.data.data;
        window.dispatchEvent(new CustomEvent('groupsUpdated'));
        setShowCreate(false);
        setName('');
        setMembers([]);
        setMemberInput('');
        navigate(`/groups/${created._id}`);
      } else {
        setCreateError(response.data.message || 'Could not create group');
      }
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Could not create group');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hub-page">
      <div className="hub-header">
        <div>
          <h1>Groups</h1>
          <p>Shared mailboxes for your team</p>
        </div>
        <button type="button" className="hub-primary-btn" onClick={() => setShowCreate(true)}>
          + New group
        </button>
      </div>

      <div className="hub-body">
        {loading && <div className="hub-state">Loading groups…</div>}
        {!loading && error && <div className="hub-state hub-error">{error}</div>}
        {!loading && !error && groups.length === 0 && (
          <div className="hub-state">
            <p>No mail groups yet.</p>
            <p className="hub-hint">Create a shared mailbox so your team can collaborate on the same inbox.</p>
            <button type="button" className="hub-primary-btn" onClick={() => setShowCreate(true)}>
              Create your first group
            </button>
          </div>
        )}
        {!loading && !error && groups.length > 0 && (
          <ul className="hub-list">
            {groups.map((group) => (
              <li key={group._id}>
                <button
                  type="button"
                  className="hub-list-item"
                  onClick={() => navigate(`/groups/${group._id}`)}
                >
                  <span className="hub-list-icon" aria-hidden="true">👨‍👩‍👧</span>
                  <span className="hub-list-copy">
                    <strong>{group.name}</strong>
                    <small>
                      {(group.members || []).length} member{(group.members || []).length === 1 ? '' : 's'}
                    </small>
                  </span>
                  <span className="hub-list-chevron" aria-hidden="true">›</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showCreate && (
        <div className="hub-modal-overlay" role="presentation" onClick={() => !saving && setShowCreate(false)}>
          <div
            className="hub-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hub-group-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="hub-group-title">New mail group</h2>
            <label className="hub-field">
              <span>Group name</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                maxLength={60}
              />
            </label>
            <label className="hub-field">
              <span>Add members (optional)</span>
              <div className="hub-chip-row">
                {members.map((email) => (
                  <span className="hub-chip" key={email}>
                    {email}
                    <button type="button" onClick={() => setMembers((prev) => prev.filter((m) => m !== email))} aria-label={`Remove ${email}`}>
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="email"
                  value={memberInput}
                  onChange={(e) => setMemberInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addMember();
                    }
                  }}
                  onBlur={addMember}
                  placeholder="user@atozasmail.in"
                />
              </div>
              <small>Only @atozasmail.in users can be added.</small>
            </label>
            {createError && <div className="hub-error">{createError}</div>}
            <div className="hub-modal-actions">
              <button type="button" className="hub-secondary-btn" onClick={() => setShowCreate(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="hub-primary-btn" onClick={handleCreate} disabled={saving || !name.trim()}>
                {saving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsHub;
